import { usePageVersions } from '@features/page-versions';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useCollection } from 'react-firebase-hooks/firestore';
import { firebase, useFirebaseSession } from '@utility/services/firebase';
import { NotificationMessage } from '@utility/notification';
import { useAuth } from '@contexts/auth';
import { Slide, ToastContainer, toast } from 'react-toastify';
import { CommonUtility } from '@utility/common';
import { useNavigate, useLocation } from 'react-router';
import { X } from '@phosphor-icons/react';

// @ts-expect-error - TS2554: Expected 1 arguments, but got 0.
const NotificationContext = createContext();

function CloseButton({ closeToast }: { closeToast: () => void }) {
  return (
    <button
      type="button"
      aria-label="Close"
      onClick={closeToast}
      className="sk:m-0 sk:flex sk:cursor-pointer sk:appearance-none sk:items-center sk:justify-center sk:border-0 sk:bg-transparent sk:p-0 sk:text-surface"
    >
      <X size={18} />
    </button>
  );
}

export function NotificationProvider({ children }) {
  const [refresh, setRefresh] = useState(0);
  const [oldNotifications, setOldNotifications] = useState(null);
  const [latestNotifications, setLatestNotifications] = useState([]);
  const [completeData, setCompleteData] = useState([]);
  const { currentOrg: organizationId, isAuthenticated, user } = useAuth();
  const [openToaster, setOpenToaster] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const route = location.pathname;

  /* The legacy feed (written by the legacy Lambdas): a V2 account gets none of its toasts. */
  const { known: versionsKnown, isV2 } = usePageVersions();
  const legacyFeed = versionsKnown && !isV2('orders');
  const firebaseReady = useFirebaseSession(!!organizationId && legacyFeed);
  const [firestoreData] = useCollection(
    organizationId && firebaseReady && legacyFeed
      ? firebase
          .firestore()
          .collection(`notifications/${organizationId}/messages`)
          .orderBy('createdAt', 'desc')
      : undefined,
  );

  // Reset latest notifications when location changes
  useEffect(() => {
    setLatestNotifications([]);
  }, [location.pathname]);

  useEffect(
    () => () => {
      setOldNotifications(null);
    },
    [organizationId],
  );

  useEffect(() => {
    if (!organizationId) return;

    // @ts-expect-error - TS2339: Property 'data' does not exist on type 'never'.
    const newNotifications = completeData.map(noti => noti.data());

    let olderNotifications = [];

    if (oldNotifications) olderNotifications = [...oldNotifications];
    let counter = openToaster;

    const newerNotifications = [];

    const roles = ['user', 'editor', 'admin', 'superadmin'];

    newNotifications.forEach(notification => {
      if (notification?.permission === 'hide') return;

      if (notification?.permission) {
        const permissionIndex = roles.findIndex(
          x => notification?.permission === x,
        );
        const rolesIndex = roles.findIndex(x => user?.role === x);

        if (
          rolesIndex >= permissionIndex ||
          notification.metadata?.purchaseOrderId ||
          notification.metadata?.userId === user?._id
        ) {
          const findIndex = (oldNotifications || []).findIndex(
            // @ts-expect-error - TS2339: Property 'id' does not exist on type 'never'.
            x => x.id === notification.id,
          );
          if (findIndex < 0 && notification.body) {
            // @ts-expect-error - TS2345: Argument of type 'any' is not assignable to parameter of type 'never'.
            olderNotifications.push(notification);
            // @ts-expect-error - TS2345: Argument of type 'any' is not assignable to parameter of type 'never'.
            if (oldNotifications) newerNotifications.push({ ...notification });
          } else if (findIndex > -1) {
            // @ts-expect-error - TS2322: Type 'any' is not assignable to type 'never'.
            olderNotifications[findIndex] = notification;
          }
          return;
        }
      }

      const findIndex = (oldNotifications || []).findIndex(
        // @ts-expect-error - TS2339: Property 'id' does not exist on type 'never'.
        x => x.id === notification.id,
      );
      if (findIndex < 0 && notification.body) {
        // @ts-expect-error - TS2345: Argument of type 'any' is not assignable to parameter of type 'never'.
        olderNotifications.push(notification);
        // @ts-expect-error - TS2345: Argument of type 'any' is not assignable to parameter of type 'never'.
        if (oldNotifications) newerNotifications.push({ ...notification });
      } else if (findIndex > -1) {
        // @ts-expect-error - TS2322: Type 'any' is not assignable to type 'never'.
        olderNotifications[findIndex] = notification;
      }
    });

    newerNotifications.forEach(notification => {
      // @ts-expect-error - TS2339: Property 'permission' does not exist on type 'never'.
      if (notification?.permission) {
        const permissionIndex = roles.findIndex(
          // @ts-expect-error - TS2339: Property 'permission' does not exist on type 'never'.
          x => notification?.permission === x,
        );
        const rolesIndex = roles.findIndex(x => user?.role === x);
        if (
          rolesIndex < permissionIndex ||
          // @ts-expect-error - TS2339: Property 'metadata' does not exist on type 'never'.
          notification.metadata?.userId === user?._id
        ) {
          return;
        }
      }

      if (counter < 5) {
        counter += 1;
        setOpenToaster(counter);
        if (counter === 5) {
          NotificationMessage.warning(
            <span>
              You have some additional notifications. <u>View All</u>
            </span>,
            '',
            {
              onClick: () => {
                navigate('/app/notifications');
              },
            },
          );
          return;
        }

        // @ts-expect-error - TS2339: Property 'status' does not exist on type 'never'.
        switch (notification.status) {
          case 'success':
            NotificationMessage.success(
              // @ts-expect-error - TS2339: Property 'body' does not exist on type 'never'.
              notification.body,
              // @ts-expect-error - TS2339: Property 'iconType' does not exist on type 'never'.
              notification.iconType,
              {
                onClose: () => {
                  counter -= 1;
                  setOpenToaster(counter);
                },
              },
            );
            break;
          case 'error':
            NotificationMessage.error(
              // @ts-expect-error - TS2339: Property 'body' does not exist on type 'never'.
              notification.body,
              // @ts-expect-error - TS2339: Property 'iconType' does not exist on type 'never'.
              notification.iconType,
              {
                onClose: () => {
                  counter -= 1;
                  setOpenToaster(counter);
                },
              },
            );
            break;
          case 'warning':
            NotificationMessage.warning(
              // @ts-expect-error - TS2339: Property 'body' does not exist on type 'never'.
              notification.body,
              // @ts-expect-error - TS2339: Property 'iconType' does not exist on type 'never'.
              notification.iconType,
              {
                onClose: () => {
                  counter -= 1;
                  setOpenToaster(counter);
                },
              },
            );
            break;
          default:
            // @ts-expect-error - TS2339: Property 'body' does not exist on type 'never'.
            NotificationMessage.info(notification.body, notification.iconType, {
              onClose: () => {
                counter -= 1;
                setOpenToaster(counter);
              },
            });
            break;
        }
      }
    });

    setLatestNotifications([...newerNotifications]);
    if (olderNotifications.length > 0) {
      // @ts-expect-error - TS2345: Argument of type 'never[]' is not assignable to parameter of type 'SetStateActio...
      setOldNotifications(olderNotifications);
    }
  }, [completeData]);

  useEffect(() => {
    if (firestoreData) {
      // @ts-expect-error - TS2345: Argument of type 'QueryDocumentSnapshot<DocumentData>[]' is not assignable to pa...
      setCompleteData(firestoreData.docs);
    }
  }, [firestoreData]);

  const update = () => {
    setRefresh(refresh + 1);
  };

  const resetLatestNotifications = () => {
    setLatestNotifications([]);
  };

  const unreadNotificationsCount = () => {
    let count = 0;
    // @ts-expect-error - TS2339: Property 'forEach' does not exist on type 'never'.
    oldNotifications?.forEach(notification => {
      if (
        CommonUtility.isValidArray(notification?.seen) &&
        !notification.seen.includes(user?._id)
      ) {
        count += 1;
      }
    });
    return count;
  };

  const publicView = useMemo(() => route?.indexOf('/public/') === 0, [route]);

  const showNotificaitons = useMemo(() => {
    if (publicView) return false;
    if (!isAuthenticated) return false;
    return true;
  }, [isAuthenticated, publicView]);

  useEffect(() => {
    if (!showNotificaitons) {
      toast.dismiss();
    }
  }, [showNotificaitons]);

  return (
    <NotificationContext.Provider
      value={{
        refresh,
        update,
        completeData,
        unreadNotificationsCount,
        notifications: oldNotifications,
        latestNotifications,
        resetLatestNotifications,
      }}
    >
      {showNotificaitons && (
        <ToastContainer
          className="sk:left-[260px] sk:w-[400px] sk:[&_div]:rounded-lg"
          enableMultiContainer
          position="bottom-left"
          containerId="notification"
          transition={Slide}
          newestOnTop
          limit={5}
          closeButton={CloseButton}
        />
      )}
      {children}
    </NotificationContext.Provider>
  );
}

export const useNotification = () => useContext(NotificationContext);
